/**
 * Drawing Tools Type Definitions
 *
 * Standalone types for the chart drawing system.
 * These types are separate from existing DataLayers types to avoid coupling.
 */

// Data point in chart coordinates (zoom-independent)
export interface DrawingDataPoint {
  x: number | Date;
  y: number;
}

// Bounding box for hit testing
export interface DrawingBoundingBox {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

// Style properties for shapes
export interface DrawingShapeStyle {
  strokeColor: string;
  strokeWidth: number;
  strokeDash: 'solid' | 'dash' | 'dot' | 'dashdot';
  fillColor?: string;
  fillOpacity?: number;
  // For line type conversion
  curveType?: 'linear' | 'spline' | 'bezier';
}

// Shape types
export type DrawingShapeType = 'point' | 'line' | 'rect' | 'ellipse' | 'freehand' | 'curve' | 'text';

// Drawing tool types (includes 'select' for selection mode)
export type DrawingToolType = DrawingShapeType | 'select';

// Base shape interface
export interface DrawingShapeBase {
  id: string;
  chartId: string;
  type: DrawingShapeType;
  style: DrawingShapeStyle;
  bounds: DrawingBoundingBox;
  createdAt: number;
  modifiedAt: number;
  selected?: boolean;
  locked?: boolean;
}

// Point marker shape: single point with label
export interface DrawingPointShape extends DrawingShapeBase {
  type: 'point';
  position: DrawingDataPoint;
  label?: string;
  symbol?: 'circle' | 'square' | 'diamond' | 'triangle' | 'cross' | 'x';
  symbolSize?: number;
}

// Line shape: two endpoints
export interface DrawingLineShape extends DrawingShapeBase {
  type: 'line';
  start: DrawingDataPoint;
  end: DrawingDataPoint;
}

// Rectangle shape: two corners
export interface DrawingRectShape extends DrawingShapeBase {
  type: 'rect';
  topLeft: DrawingDataPoint;
  bottomRight: DrawingDataPoint;
  cornerRadius?: number;
}

// Ellipse shape: center + radii
export interface DrawingEllipseShape extends DrawingShapeBase {
  type: 'ellipse';
  center: DrawingDataPoint;
  radiusX: number;
  radiusY: number;
}

// Freehand shape: array of points
export interface DrawingFreehandShape extends DrawingShapeBase {
  type: 'freehand';
  points: DrawingDataPoint[];
  smoothed?: boolean;
}

// Curve shape: bezier with control points
export interface DrawingCurveShape extends DrawingShapeBase {
  type: 'curve';
  start: DrawingDataPoint;
  end: DrawingDataPoint;
  controlPoint1: DrawingDataPoint;
  controlPoint2: DrawingDataPoint;
}

// Text annotation shape
export interface DrawingTextShape extends DrawingShapeBase {
  type: 'text';
  position: DrawingDataPoint;
  text: string;
  fontSize: number;
  fontFamily: string;
}

// Union type for all shapes
export type DrawingShape =
  | DrawingPointShape
  | DrawingLineShape
  | DrawingRectShape
  | DrawingEllipseShape
  | DrawingFreehandShape
  | DrawingCurveShape
  | DrawingTextShape;

// Drawing mode state machine
export type DrawingMode =
  | 'idle'
  | 'ready'      // Tool selected, waiting for first click
  | 'drawing'    // Actively drawing
  | 'selected'   // Shape(s) selected
  | 'moving'     // Moving selected shape
  | 'resizing';  // Resizing selected shape

// Sync scope for multi-chart sync
export type DrawingSyncScope = 'none' | 'section' | 'all';

// Drawing state
export interface DrawingState {
  mode: DrawingMode;
  activeTool: DrawingToolType | null;
  shapes: DrawingShape[];
  selectedShapeIds: string[];
  activeChartId: string | null;

  // For in-progress drawing
  tempShape: Partial<DrawingShape> | null;
  tempPoints: DrawingDataPoint[];

  // Sync configuration
  syncEnabled: boolean;
  syncScope: DrawingSyncScope;

  // History for undo/redo
  historyIndex: number;
  history: DrawingShape[][];
}

// Default style for new shapes
export const DEFAULT_SHAPE_STYLE: DrawingShapeStyle = {
  strokeColor: '#6366f1', // Indigo
  strokeWidth: 2,
  strokeDash: 'solid',
  fillColor: undefined,
  fillOpacity: 0.1,
  curveType: 'linear',
};

// Tool display configuration
export interface DrawingToolConfig {
  type: DrawingToolType;
  label: string;
  icon: string; // Lucide icon name
  shortcut?: string;
}

export const DRAWING_TOOLS: DrawingToolConfig[] = [
  { type: 'select', label: 'Select', icon: 'MousePointer2', shortcut: 'V' },
  { type: 'point', label: 'Point Marker', icon: 'MapPin', shortcut: 'M' },
  { type: 'line', label: 'Line', icon: 'Minus', shortcut: 'L' },
  { type: 'rect', label: 'Rectangle', icon: 'Square', shortcut: 'R' },
  { type: 'ellipse', label: 'Ellipse', icon: 'Circle', shortcut: 'E' },
  { type: 'freehand', label: 'Freehand', icon: 'Pencil', shortcut: 'P' },
  { type: 'curve', label: 'Curve', icon: 'Spline', shortcut: 'C' },
  { type: 'text', label: 'Text', icon: 'Type', shortcut: 'T' },
];

// Stroke dash patterns for Plotly
export const STROKE_DASH_PATTERNS: Record<DrawingShapeStyle['strokeDash'], string> = {
  solid: 'solid',
  dash: 'dash',
  dot: 'dot',
  dashdot: 'dashdot',
};
